// Form funnel tracking for the first-party collector.
//
// GA4's enhanced measurement gives you form_start / form_submit but attributes
// them to an auto-detected form id, which on this site is useless (most forms
// are unnamed divs). This gives every form an explicit, stable name and the
// four events that actually answer "where do people drop out":
//
//   form_start    { form }                  first real interaction, once per cycle
//   form_submit   { form }                  a submit was attempted, once per cycle
//   form_success  { form }                  the submit succeeded
//   form_error    { form, reason, field? }  validation or server failure
//
// Counting rules, because the funnel percentages are only meaningful if these
// hold:
//
//  - start and submit are each emitted AT MOST ONCE per attempt cycle, so
//    started >= attempted >= succeeded and no ratio can exceed 100%. A user who
//    fixes two validation errors is one attempt, not three; the retries stay
//    visible as form_error rows instead.
//  - submitted() implies start. A visitor can reach submit without focusing a
//    tracked field (ticking only the consent box), which would otherwise report
//    an attempt with no start and a ratio above 100%.
//  - success and failure are mutually exclusive and terminal. Without that, a
//    flow that reports success and then throws while navigating (KitGate does
//    exactly this if res.kitUrl is malformed) lands in both the conversion
//    count and the error table for one submission.
//  - reset() starts a new cycle, for forms whose component is created once but
//    whose fields are cleared and reshown (the modals in Trust.vue, pricing).
//
// NOTE on the GTM path: the collector distinguishes an attempt from a success
// via separate event NAMES, but GTM triggers key on the name too, so the
// dataLayer gets `form_submit_attempt` for the attempt and `form_submit` only
// on success, which is the name existing triggers already expect.
//
// Usage in a form component:
//
//   const form = useFormTracking('contact')
//   ...
//   <input @focus="form.start()" v-model="email" />
//   ...
//   const submit = async () => {
//     form.submitted()                       // attempt
//     try { await post(); form.ok() }        // success
//     catch (e) { form.failed(e.message) }   // failure
//   }
//
// Client-only and best-effort: it never throws and no-ops during SSR.
export const useFormTracking = (formName) => {
  if (process.server) {
    return {
      start: () => {},
      submitted: () => {},
      ok: () => {},
      failed: () => {},
      reset: () => {},
    }
  }

  const track = useTrack()
  let started = false
  let attempted = false
  let settled = false

  // `event` is what the first-party collector records; `gtmEvent` is what goes
  // to the dataLayer, which is not always the same name (see the note above).
  // Passing gtmEvent === null suppresses the dataLayer push entirely.
  // Wrapped as a whole. These methods are called from inside submit handlers,
  // so an error here would break the form rather than merely lose a metric.
  const push = (event, extra = {}, gtmEvent = event) => {
    try {
      // GTM/GA4 path + first-party collector, mirroring useArticleEngagement so
      // both surfaces see the funnel even when third-party analytics is blocked.
      if (gtmEvent && window.dataLayer) {
        window.dataLayer.push({ event: gtmEvent, form: formName, ...extra })
      }
      track(event, { form: formName, ...extra })
    } catch {
      /* never throw from analytics */
    }
  }

  const start = () => {
    if (started) return
    started = true
    push('form_start')
  }

  return {
    // First genuine interaction with the form.
    start,

    // A submit was attempted.
    submitted: () => {
      start() // an attempt always implies a start
      if (attempted) return
      attempted = true
      push('form_submit', {}, 'form_submit_attempt')
    },

    // The submit succeeded (lead created / subscribed / registered).
    // A DISTINCT collector event name. The rollup's generic 'event' metric
    // counts every row, so sharing a name with the attempt would make the
    // events list report attempts + successes together and overstate
    // conversions.
    ok: (extra = {}) => {
      if (settled) return
      settled = true
      // A server-side spam decoy returns ok:true with recorded:false. The call
      // sites all refuse to push elker_conversion for those, so pushing
      // `form_submit` to the dataLayer would fire the very GA4/Ads trigger that
      // gate exists to suppress. Record it first-party, keep it out of GTM.
      const isDecoy = extra.recorded === false
      push('form_success', { ...extra }, isDecoy ? null : 'form_submit')
    },

    // The submit failed. `reason` should be short and non-identifying.
    // Validation failures happen before the form settles, so they still report;
    // a failure AFTER a success (a post-conversion navigation throwing) is
    // dropped, because the submission did succeed.
    failed: (reason, field = null) => {
      if (settled) return
      push('form_error', {
        reason: typeof reason === 'string' ? reason.slice(0, 120) : 'unknown',
        ...(field ? { field } : {}),
        attempted,
      })
    },

    // Begin a new attempt cycle. Call this when a form is reopened or cleared
    // for reuse, so the next interaction records a fresh start.
    reset: () => {
      started = false
      attempted = false
      settled = false
    },
  }
}
